import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Building2, Network, ShoppingBag, Check, ArrowRight, Sparkles, Users, Layers, Clock } from "lucide-react";
import TileSphereLogo from "@/components/TileSphereLogo";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PORTALS = [
  { key: "ORGANIZATION", icon: Building2, color: "from-emerald-500 to-teal-600", tag: "Manufacturers & Orgs" },
  { key: "VENDOR", icon: ShoppingBag, color: "from-pink-500 to-fuchsia-600", tag: "Vendors" },
  { key: "DISTRIBUTOR", icon: Network, color: "from-blue-500 to-indigo-600", tag: "Distributors" },
];

function currency(n) { try { return `₹${Number(n || 0).toLocaleString()}`; } catch { return `₹${n}`; } }

export default function SubscriptionPage() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const initialPortal = (params.get("portal") || "ORGANIZATION").toUpperCase();
  const [portal, setPortal] = useState(PORTALS.find((p) => p.key === initialPortal) ? initialPortal : "ORGANIZATION");
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    axios.get(`${API}/platform/public/plans`).then(({ data }) => setPlans(data || []));
  }, []);

  useEffect(() => { setParams({ portal }); }, [portal, setParams]);

  const rolePlans = useMemo(() => {
    const p = portal.toLowerCase();
    return plans.filter((pl) => {
      if (pl.portal_kind && pl.portal_kind !== portal) return false;
      if (pl.trial_portal && pl.trial_portal !== portal) return false;
      // Non-trial plans without a portal_kind are shown for ORG (legacy)
      if (!pl.is_trial && !pl.portal_kind && !pl.trial_portal) return portal === "ORGANIZATION";
      return true;
    }).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || (a.monthly_price || 0) - (b.monthly_price || 0));
  }, [plans, portal]);

  const startRegister = (planCode) => nav(`/register?portal=${portal}&plan=${planCode}`);

  return (
    <div className="min-h-screen bg-[#0a0a14] text-white overflow-x-hidden" data-testid="subscribe-page">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-black/40 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/"><TileSphereLogo /></Link>
          <div className="flex items-center gap-2">
            <LanguageSwitcher variant="dark" />
            <Link to="/login"><Button variant="ghost" className="text-white hover:bg-white/10" data-testid="subscribe-signin">{t("common.signIn")}</Button></Link>
          </div>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-12 pb-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/15 text-[11px] font-bold uppercase tracking-[0.25em] text-orange-300">
            <Sparkles className="w-3 h-3" /> Three portals · One ecosystem
          </div>
          <h1 className="font-display text-5xl sm:text-6xl font-black mt-4 leading-[0.95]">
            Pick your portal.<br />
            <span className="bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent">See your plans.</span>
          </h1>
          <p className="mt-4 text-white/60 max-w-xl">Every portal has its own Free trial + Paid plans, curated by our Super Admin team. Choose your role below.</p>
        </motion.div>

        {/* Compact 3-icon role picker */}
        <div className="mt-10 grid grid-cols-3 gap-3 sm:gap-4 max-w-2xl" data-testid="portal-picker">
          {PORTALS.map((p) => {
            const active = portal === p.key;
            const Icon = p.icon;
            return (
              <button
                key={p.key}
                onClick={() => setPortal(p.key)}
                data-testid={`portal-pick-${p.key.toLowerCase()}`}
                className={`relative rounded-2xl p-4 sm:p-5 border transition-all ${active ? "border-white/40 bg-white/5" : "border-white/10 bg-white/[0.02] hover:bg-white/[0.05]"}`}
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${p.color} flex items-center justify-center mb-2 sm:mb-3 ${active ? "shadow-[0_0_30px_rgba(255,255,255,0.15)]" : ""}`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">{p.tag}</div>
                  <div className="font-display text-base sm:text-lg font-bold">{p.key.charAt(0) + p.key.slice(1).toLowerCase()}</div>
                </div>
                {active && <div className="absolute top-3 right-3 w-2 h-2 rounded-full bg-orange-400" />}
              </button>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="plans-grid">
          {rolePlans.length === 0 && (
            <div className="col-span-full text-center text-white/40 py-16">No plans configured for this portal yet. Contact sales.</div>
          )}
          {rolePlans.map((pl, i) => {
            const price = pl.monthly_price || pl.base_price_monthly || 0;
            const isTrial = pl.is_trial;
            const users = pl.max_users_default || pl.trial_max_users || null;
            const processes = (pl.features || []).length;
            const trialDays = pl.trial_config?.days || pl.trial_days || null;
            return (
              <motion.div
                key={pl.id || pl.code}
                initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                whileHover={{ y: -6 }}
                className={`relative rounded-3xl border ${isTrial ? "border-orange-400/40 bg-gradient-to-br from-orange-500/[0.08] to-pink-500/[0.04]" : "border-white/10 bg-white/[0.03]"} backdrop-blur-xl p-6 flex flex-col`}
                data-testid={`plan-card-${pl.code}`}
              >
                {isTrial && <Badge className="absolute top-4 right-4 bg-orange-400/20 text-orange-200 border-orange-400/40">Free trial</Badge>}
                <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold">{pl.code}</div>
                <div className="font-display text-2xl font-black mt-1">{pl.name}</div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-display text-4xl font-black">{isTrial ? "₹0" : currency(price)}</span>
                  {!isTrial && <span className="text-sm text-white/50">/mo</span>}
                </div>
                {pl.description && <p className="mt-2 text-sm text-white/60">{pl.description}</p>}
                {pl.tagline && !pl.description && <p className="mt-2 text-sm text-white/60">{pl.tagline}</p>}

                {/* At-a-glance metrics: users, processes, trial duration */}
                <div className="mt-4 grid grid-cols-3 gap-2 rounded-2xl bg-black/30 border border-white/10 p-3" data-testid={`plan-metrics-${pl.code}`}>
                  <div className="text-center">
                    <Users className="w-4 h-4 mx-auto text-orange-300" />
                    <div className="font-display text-lg font-black mt-1" data-testid={`plan-users-${pl.code}`}>{users ?? "∞"}</div>
                    <div className="text-[9px] uppercase tracking-widest text-white/50">users</div>
                  </div>
                  <div className="text-center border-x border-white/10">
                    <Layers className="w-4 h-4 mx-auto text-emerald-300" />
                    <div className="font-display text-lg font-black mt-1" data-testid={`plan-processes-${pl.code}`}>{processes}</div>
                    <div className="text-[9px] uppercase tracking-widest text-white/50">processes</div>
                  </div>
                  <div className="text-center">
                    <Clock className="w-4 h-4 mx-auto text-blue-300" />
                    <div className="font-display text-lg font-black mt-1" data-testid={`plan-trial-${pl.code}`}>{isTrial ? `${trialDays || 14}d` : "—"}</div>
                    <div className="text-[9px] uppercase tracking-widest text-white/50">{isTrial ? "trial" : "no trial"}</div>
                  </div>
                </div>

                <ul className="mt-5 space-y-2 flex-1">
                  {(pl.features || []).slice(0, 8).map((f, idx) => (
                    <li key={idx} className="text-sm text-white/80 flex items-start gap-2">
                      <Check className="w-4 h-4 mt-0.5 text-emerald-400 flex-shrink-0" /> <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => startRegister(pl.code)}
                  data-testid={`plan-cta-${pl.code}`}
                  className={`mt-6 h-11 font-bold ${isTrial ? "bg-gradient-to-r from-orange-500 to-pink-500 hover:from-orange-600 hover:to-pink-600" : "bg-white text-black hover:bg-white/90"}`}
                >
                  {isTrial ? "Start free trial" : "Register & pay"} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </motion.div>
            );
          })}
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 px-6 bg-black text-white/50 text-sm text-center">
        © TileSphere · Plans powered by TileSphere Super Admin
      </footer>
    </div>
  );
}
